import { zLayer } from "@sellar-io/components";
import { ReactElement, useLayoutEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { Alert } from "../alert/Alert";

const hiddenPlatformBannerHeightProperty = "--hidden-platform-banner-height";

export const HiddenPlatformBanner = ({
  isMobile,
}: {
  isMobile: boolean;
}): ReactElement => {
  const bannerRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isMobile) return;

    const banner = bannerRef.current;
    const spacer = spacerRef.current;
    if (!banner || !spacer) return;

    const updateBannerHeight = () => {
      const height = `${banner.getBoundingClientRect().height}px`;
      spacer.style.height = height;
      document.documentElement.style.setProperty(
        hiddenPlatformBannerHeightProperty,
        height,
      );
    };

    updateBannerHeight();
    const resizeObserver = new ResizeObserver(updateBannerHeight);
    resizeObserver.observe(banner);

    return () => {
      resizeObserver.disconnect();
      document.documentElement.style.removeProperty(
        hiddenPlatformBannerHeightProperty,
      );
    };
  }, [isMobile]);

  const banner = (
    <Alert
      type="sellar"
      title={isMobile ? "" : "Your platform is currently hidden"}
      message={
        <>
          {isMobile ? (
            <span className="font-semibold">You are in hidden mode </span>
          ) : (
            "Any changes you make won't be seen by customers whilst in hidden mode. Let your account manager know when you're ready to go live. "
          )}
          <a
            className="font-semibold text-[#8c23c7] underline"
            href="https://intercom.help/sellario/en/collections/10046074-guides-for-makers"
            target="_blank"
            rel="noreferrer"
          >
            View help guides ↗︎
          </a>
        </>
      }
      className="w-full border-zinc-300 bg-zinc-50"
    />
  );

  if (!isMobile) return <div className="p-1">{banner}</div>;

  return (
    <>
      <div ref={spacerRef} aria-hidden="true" className="w-full shrink-0" />
      <div
        ref={bannerRef}
        className={twMerge(
          `fixed top-[var(--refresh-pwa-banner-height)] right-0 left-0 ${zLayer.banner}`,
          "p-1 print:hidden",
        )}
      >
        {banner}
      </div>
    </>
  );
};
